import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TaxonomyType } from '@app/model/enums/taxonomy-type.enum';
import { PayableNode } from '@app/model/payable-node';
import { SearchService } from '@app/services/api/search.service';
import { TaxonomyApiService } from '@app/services/api/taxonomy-api.service';
import { PayableNodeNavigationService } from '@app/services/payable-node-navigation.service';
import { BundleType, bundleTypeTextMapPlural, NodeModel, STORAGE_KEYS, StorageKeys, StorageService } from 'library-explorer';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { debounceTime, exhaustMap, finalize, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-quick-search-input',
    templateUrl: './quick-search-input.component.html',
    styleUrls: ['./quick-search-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuickSearchInputComponent implements OnInit, OnDestroy {
  @Input() public bundle: BundleType | BundleType[];

  @Input() public dialogMode = false;
  @Input() public enableLastSearch = false;

  @Input() public placeholder: string;
  @Input() public ariaLabel: string;
  
  @Input() public resultTemplate: TemplateRef<any>;
  @Input() public resultsHeaderTemplate: TemplateRef<any>;
  @Input() public enabledSearchByTag = false;

  @Input() public selectionMode = false;
  @Input() public pageSize = 3;
  @Input() public showResultsWithoutSearch = false;
  @Input() public enableLoadMore = false;
  @Input() public excludedBundles: BundleType[] = [];

  @Output() public search: EventEmitter<string> = new EventEmitter();
  @Output() public searchByTag: EventEmitter<string> = new EventEmitter();
  @Output() public searchByBundle: EventEmitter<BundleType> = new EventEmitter();
  @Output() public closeSearch: EventEmitter<void> = new EventEmitter();
  @Output() public itemSelected: EventEmitter<NodeModel> = new EventEmitter();

  public searchControl = new FormControl('');

  public tags = [];
  public tagsLoading = true;

  public data = [];
  public dataGroups: { bundle: string, label: string, items: NodeModel[] } [] = [];
  public dataLoading = true;

  public loadingPlaceholers = new Array(3);

  public expanded = false;

  public availableBundles = [];

  public resultsBundles = [];

  public lastSearches = [];

  public readonly bundles: typeof BundleType = BundleType;

  public bundleSetInternally = false;
  public isLoadingMore = false;
  public hasMoreResults = false;

  @ViewChild('loadMoreSentinel') private set loadMoreSentinelRef(ref: ElementRef<HTMLElement> | undefined) {
    this.observeLoadMoreSentinel(ref?.nativeElement);
  }

  private unsubscribe: Subject<void> = new Subject();

  private readonly minSearchLength = 3;
  private singleBundleOffset = 0;
  private loadMoreSentinelObserver: IntersectionObserver | null = null;
  private searchRequestToken = 0;
  private groupedPreviewSnapshot: { searchKey: string; data: NodeModel[]; dataGroups: any[]; resultsBundles: BundleType[] } | null = null;

  constructor(
    private readonly storageService: StorageService,
    @Inject(STORAGE_KEYS) private readonly storageKeys: StorageKeys,
    private readonly payableNodeNavigationService: PayableNodeNavigationService,
    private readonly searchService: SearchService,
    private readonly taxonomyApiService: TaxonomyApiService,
    private readonly ngZone: NgZone) { }

  ngOnInit(): void {
    this.loadAvailableBundles();
    this.addSubscriptionsForSearch();
    this.setLastSearchesFromLS();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.loadMoreSentinelObserver?.disconnect();
  }

  public submitSearchQuery(): void {
    if (this.searchControl?.value?.length < this.minSearchLength) {
      return;
    }

    this.setCurrentSearchToLS();
    this.search.emit(this.searchControl?.value);
  }

  public navigateToEntity(item: Partial<PayableNode>): void {
    if (item.isLocked) {
      return;
    }

    this.setCurrentSearchToLS(item.title);

    if (this.selectionMode) {
      this.itemSelected.emit(item as NodeModel);
    } else {
      this.payableNodeNavigationService.navigate(item);
    }

    this.colapseSearchPanel();
    this.clearSearch();
  }

  public setBundle(bundle: BundleType): void {
    if (!this.groupedPreviewSnapshot) {
      this.groupedPreviewSnapshot = {
        searchKey: this.searchControl?.value,
        data: this.data,
        dataGroups: this.dataGroups,
        resultsBundles: this.resultsBundles
      };
    }

    this.bundle = bundle;
    this.bundleSetInternally = true;
    this.expanded = true;

    this.loadData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe();
  }

  public clearBundleFilter(): void {
    if (!this.bundleSetInternally) {
      return;
    }

    this.bundle = null;
    this.bundleSetInternally = false;

    const snapshot = this.groupedPreviewSnapshot;
    this.groupedPreviewSnapshot = null;

    if (snapshot && snapshot.searchKey === this.searchControl?.value) {
      this.searchRequestToken++;

      this.data = snapshot.data;
      this.dataGroups = snapshot.dataGroups;
      this.resultsBundles = snapshot.resultsBundles;
      this.singleBundleOffset = 0;
      this.hasMoreResults = false;
      this.dataLoading = false;
      return;
    }

    this.loadData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe();
  }

  public loadMoreResults(): void {
    if (this.isLoadingMore || !this.hasMoreResults || !this.bundle || !this.enableLoadMore) {
      return;
    }

    this.isLoadingMore = true;
    const nextOffset = this.singleBundleOffset + this.pageSize;
    const requestToken = this.searchRequestToken;

    this.searchService.searchBy(this.searchControl?.value, this.bundle, nextOffset, this.pageSize + 1)
      .pipe(
        finalize(() => this.isLoadingMore = false),
        takeUntil(this.unsubscribe)
      )
      .subscribe(response => {
        if (requestToken !== this.searchRequestToken) {
          return;
        }

        const items = response?.items ?? [];

        this.singleBundleOffset = nextOffset;
        this.hasMoreResults = items.length > this.pageSize;
        this.setData([...this.data, ...items.slice(0, this.pageSize)]);
      });
  }

  public expandSearchPanel(): void {
    this.expanded = this.showResultsWithoutSearch || this.searchControl?.value?.length >= this.minSearchLength;
  }

  public colapseSearchPanel(): void {
    this.expanded = false;
  }

  public clearSearch(): void {
    this.searchControl.setValue('');
  }

  public closeSearchDialog(): void {
    this.closeSearch.emit();
  }

  public useLastSearchItem(data: { search: string }): void {
    if (!data) {
      return;
    }

    const { search } = data;

    this.searchControl.patchValue(search);
  }

  
  public clearLastSearchItem(event: Event, data: { search: string }): void {
    event.stopPropagation();
  
    if (!data) {
      return;
    }

    this.lastSearches = this.lastSearches.filter(item => item?.search !== data.search);
    this.storageService.setItem(this.storageKeys.LAST_SEARCH, this.lastSearches);
  }


  private observeLoadMoreSentinel(sentinel: HTMLElement | undefined): void {
    this.loadMoreSentinelObserver?.disconnect();
    this.loadMoreSentinelObserver = null;

    if (!sentinel) {
      return;
    }

    this.loadMoreSentinelObserver = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        this.ngZone.run(() => this.loadMoreResults());
      }
    }, { rootMargin: '200px' });

    this.loadMoreSentinelObserver.observe(sentinel);
  }

  private loadAvailableBundles(): void {
    this.searchService.getAvailableSearchBundles()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.availableBundles = data.filter(bundle => !this.excludedBundles.includes(bundle));
        this.resultsBundles = [...this.availableBundles];

        if (this.showResultsWithoutSearch) {
          this.expanded = true;

          forkJoin([this.loadTags(), this.loadData()])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe();
        }
      });
  }

  private loadTags(): Observable<any> {
    this.tagsLoading = true;

    const params = new HttpParams({ fromObject: { search: this.searchControl?.value, limit: 3 } });

    return this.taxonomyApiService.getTaxonomiesList(TaxonomyType.PUBLIC_TAGS, params)
      .pipe(
        tap(() => this.tagsLoading = false),
        map(data => this.tags = data.items)
      );
  }

  private loadData(): Observable<any> {
    this.dataLoading = true;
    this.setData([]);
    this.singleBundleOffset = 0;
    this.hasMoreResults = false;

    const requestToken = ++this.searchRequestToken;
    const singleBundleLimit = this.enableLoadMore ? this.pageSize + 1 : this.pageSize;

    const mainSearchRequest = this.bundle
      ? (this.bundle?.length
        ? this.searchService.searchBy(this.searchControl?.value, this.bundle, 0, singleBundleLimit)
        : of(null))
      : (this.availableBundles?.length
        ? this.searchService.groupSearch(this.searchControl?.value, this.availableBundles, 3)
        : of(null));

    return mainSearchRequest
      .pipe(
        tap(() => {
          if (requestToken === this.searchRequestToken) {
            this.dataLoading = false;
          }
        }),
        map(data => {
          if (requestToken !== this.searchRequestToken) {
            return;
          }

          const items = data?.items || [];

          this.resultsBundles = this.availableBundles.filter(bundle => items.some(item => item.bundle === bundle));

          if (this.bundle && this.enableLoadMore) {
            this.hasMoreResults = items.length > this.pageSize;
            this.setData(items.slice(0, this.pageSize));
          } else {
            this.setData(items);
          }
        })
      );
  }

  private setData(items: NodeModel[]): void {
    this.data = items;

    if (!items || items.length === 0) {
      this.dataGroups = [];
      return;
    }

    if (!this.dialogMode) {
      this.dataGroups = [{ items, bundle: null, label: null }];
      return;
    }

    this.dataGroups = items
      .reduce((groupsList, item) => {
        let group = groupsList.find(group => group.bundle === item.bundle);

        if (!group) {
          group = {
            label: bundleTypeTextMapPlural[item.bundle],
            items: [],
            bundle: item.bundle
          };

          groupsList.push(group);
        }

        group.items.push(item);

        return groupsList;
      }, [])
      .filter(group => group.items.length >= 1)
      .sort((a, b) => this.availableBundles.indexOf(a.bundle) - this.availableBundles.indexOf(b.bundle));
  }

  private addSubscriptionsForSearch(): void {
    const triggerSearch = new BehaviorSubject(false);

    const searchObs = this.searchControl.valueChanges
      .pipe(
        debounceTime(200)
      );
  
      combineLatest([
        searchObs,
        triggerSearch
      ])
      .pipe(
        debounceTime(50),
        exhaustMap(([searchKey]) => {
          this.expanded = this.showResultsWithoutSearch || searchKey?.length >= this.minSearchLength;

          if (!this.expanded) {
            this.setData([]);
            this.tags = [];
            return of(null);
          }

          return forkJoin([
            this.loadTags(),
            this.loadData()
          ])
          .pipe(
            finalize(() => {
              const changed = searchKey !== this.searchControl?.value;

              if (changed) {
                triggerSearch.next(true);
              }
            })
          )
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe();
  }

  private setLastSearchesFromLS(): void {
    this.lastSearches = this.storageService.getItem(this.storageKeys.LAST_SEARCH) || [];
  }

  private setCurrentSearchToLS(search = this.searchControl?.value): void {
    if (!this.enableLastSearch) {
      return;
    }

    if (!search) {
      return;
    }
  
    this.lastSearches = this.lastSearches.filter(item => item.search !== search)
    this.lastSearches.unshift({ search });
    this.lastSearches = this.lastSearches.slice(0, 3);
    this.storageService.setItem(this.storageKeys.LAST_SEARCH, this.lastSearches);
  }
}
